import React from "react";
import toast from "react-hot-toast";
import { Box, Icon, IconButton, Stack, Text, Tooltip } from "@chakra-ui/react";
import { MdContentCopy, MdCheckCircleOutline } from "react-icons/md";

const successBoxProps = {
    background: "rgba(76, 175, 80, 0.08)",
    border: "1px solid rgba(76, 175, 80, 0.3)",
    borderRadius: "8px",
    p: 4,
    display: "flex",
    gap: 3,
    alignItems: "flex-start",
};

const linkBoxProps = {
    background: "rgba(0, 0, 0, 0.4)",
    border: "1px solid rgba(255, 255, 255, 0.1)",
    borderRadius: "6px",
    p: 2.5,
    fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
    fontSize: "0.72rem",
    color: "rgba(255, 255, 255, 0.85)",
    wordBreak: "break-all",
};

/**
 * Copies a string to the clipboard with toast feedback.
 * @param {string} value - Value to copy
 * @returns {Promise<void>}
 */
async function copyToClipboard(value) {
    try {
        await navigator.clipboard.writeText(value);
        toast.success("Lien copié");
    } catch {
        toast.error("Impossible de copier");
    }
}

/**
 * Renders a labeled value row with optional copy button.
 * @param {Object} props
 * @param {string} props.label - Field label
 * @param {string} props.value - Field value
 * @param {boolean} props.copyable - Whether to show the copy button
 * @returns {React.ReactElement}
 */
function MetaRow({ label, value, copyable }) {
    if (!value) return null;
    return (
        <Stack direction="row" spacing={2} alignItems="center" mt={2}>
            <Text color="rgba(255, 255, 255, 0.55)" fontSize="0.7rem" minW="80px" textTransform="uppercase">
                {label}
            </Text>
            <Text color="rgba(255, 255, 255, 0.85)" fontSize="0.75rem" fontFamily="monospace" flex={1} wordBreak="break-all">
                {value}
            </Text>
            {copyable && (
                <Tooltip label="Copier" hasArrow>
                    <IconButton aria-label="Copier" size="sm" variant="ghost" onClick={() => copyToClipboard(value)}
                        color="rgba(255, 255, 255, 0.6)" icon={<Icon as={MdContentCopy} boxSize="14px" />} />
                </Tooltip>
            )}
        </Stack>
    );
}

/**
 * Renders the green success banner at the top of the panel.
 * @returns {React.ReactElement}
 */
function SuccessBanner() {
    return (
        <Box {...successBoxProps}>
            <Icon as={MdCheckCircleOutline} color="#81c784" mt={0.5} boxSize="20px" flexShrink={0} />
            <Box>
                <Text color="#a5d6a7" fontSize="0.9rem" fontWeight={600}>
                    Abonnement créé — en attente de paiement
                </Text>
                <Text color="rgba(255, 255, 255, 0.7)" fontSize="0.78rem" mt={1}>
                    Copie le lien ci-dessous et envoie-le à l'utilisateur. Le statut passera à actif dès qu'il aura saisi sa carte.
                </Text>
            </Box>
        </Box>
    );
}

/**
 * Success panel displayed after a paid subscription is created.
 * Shows the setup_link to copy and the related Stripe identifiers.
 * @param {Object} props
 * @param {Object} props.result - API response from POST /create-paid
 * @returns {React.ReactElement}
 */
function PaidSuccessPanel({ result }) {
    const link = result?.setup_link || "";
    return (
        <Stack spacing={3}>
            <SuccessBanner />
            <Box>
                <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1.5}>
                    <Text color="rgba(255, 255, 255, 0.85)" fontSize="0.78rem" fontWeight={600}>
                        Lien de configuration
                    </Text>
                    <Tooltip label="Copier" hasArrow>
                        <IconButton aria-label="Copier le lien" size="sm" variant="ghost" onClick={() => copyToClipboard(link)}
                            color="#17dcb8" icon={<Icon as={MdContentCopy} boxSize="16px" />} />
                    </Tooltip>
                </Stack>
                <Box {...linkBoxProps}>{link || "—"}</Box>
            </Box>
            <Box>
                <MetaRow label="Montant" value={result?.amount != null ? `${result.amount} ${result.currency || ""}`.trim() : ""} />
                <MetaRow label="Setup ID" value={result?.setup_intent_id} copyable />
                <MetaRow label="Sub ID" value={result?.subscription?.id} copyable />
            </Box>
        </Stack>
    );
}

export default PaidSuccessPanel;
